// Resolves null once `ms` pass, so a promise that never settles can't stall the caller.
export const settleWithin = <T>(
  promise: Promise<T>,
  ms: number
): Promise<T | null> =>
  new Promise<T | null>((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

// readyState turns 'interactive' when parsing ends, but DOMContentLoaded only
// fires after the deferred scripts have run, seconds later on a slow phone, so
// readyState alone can't tell whether the event has fired yet.
const hasDomContentLoadedFired = (): boolean => {
  if (document.readyState === 'complete') return true;
  const [navigation] = performance.getEntriesByType?.('navigation') ?? [];
  return (
    !!navigation &&
    'domContentLoadedEventStart' in navigation &&
    Number(navigation.domContentLoadedEventStart) > 0
  );
};

export const domContentLoaded = (): Promise<void> =>
  hasDomContentLoadedFired()
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        document.addEventListener('DOMContentLoaded', () => resolve(), {
          once: true
        });
      });
